import { AppDialog, AppConfirmDialog } from "components/common/dialogs";
import { AppButton } from "components/common/buttons";
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Repeat as RepeatIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { getToken } from "services/auth.service";

const EventDetailPopover = ({ open, onClose, event, onEdit, onDelete }) => {
  const [eventDetail, setEventDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchEventDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const token = getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/Events/${event.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Không thể tải chi tiết sự kiện");
        }

        const data = await response.json();
        setEventDetail(data);
      } catch (error) {
        console.error("Error fetching event detail:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (open && event?.id) {
      fetchEventDetail();
    }
  }, [open, event]);

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events/${event.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Không thể xóa sự kiện");
      }

      onDelete && onDelete(event.id);
      setDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Error deleting event:", error);
      setError(error.message);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
  };

  const handleEdit = () => {
    onEdit && onEdit(eventDetail);
    onClose();
  };

  const formatDateTime = (dateString, isAllDay = false) => {
    const date = new Date(dateString);
    if (isAllDay) {
      return format(date, "dd/MM/yyyy", { locale: vi });
    }
    return format(date, "dd/MM/yyyy HH:mm", { locale: vi });
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case "manual":
        return "Tạo thủ công";
      case "contract":
        return "Từ hợp đồng";
      case "recurring":
        return "Lặp lại";
      default:
        return type;
    }
  };

  const getRecurrenceLabel = (pattern) => {
    switch (pattern) {
      case "daily":
        return "Hàng ngày";
      case "weekly":
        return "Hàng tuần";
      case "monthly":
        return "Hàng tháng";
      case "yearly":
        return "Hàng năm";
      default:
        return pattern;
    }
  };

  if (!event) return null;

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        title="Chi tiết sự kiện"
        loading={deleting}
        actions={
          <>
            <AppButton disabled={deleting} onClick={onClose} color="inherit" variant="outlined">
              Đóng
            </AppButton>
            {eventDetail?.canEdit && (
              <>
                <AppButton disabled={deleting}
                  onClick={handleEdit}
                  color="primary"
                  variant="contained"
                >
                  Chỉnh sửa
                </AppButton>
                <AppButton disabled={deleting}
                  onClick={handleDelete}
                  color="error"
                  variant="contained"
                >
                  Xóa
                </AppButton>
              </>
            )}
          </>
        }
      >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : eventDetail ? (
            <Stack spacing={2.5} divider={<Divider />}>
              <Box component="section" aria-label="Thông tin sự kiện">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontSize: 18, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere", flex: "1 1 220px" }}
                  >
                    {eventDetail.title}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Chip
                      label={getEventTypeLabel(eventDetail.eventType)}
                      size="small"
                      color="primary"
                    />
                    {eventDetail.isRecurring && eventDetail.eventType !== "recurring" && (
                      <Chip icon={<RepeatIcon />} label="Lặp lại" size="small" color="secondary" />
                    )}
                  </Box>
                </Box>

                {eventDetail.description && (
                  <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {eventDetail.description}
                  </Typography>
                )}

                {eventDetail.contractId && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                      <BusinessIcon sx={{ fontSize: 18 }} />
                      Hợp đồng liên kết
                    </Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                      <strong>Số HĐ:</strong> {eventDetail.contractNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
                      <strong>Tên:</strong> {eventDetail.contractName}
                    </Typography>
                    {eventDetail.itemId && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1, overflowWrap: "anywhere" }}>
                          <strong>Mã hạng mục:</strong> {eventDetail.itemCode}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
                          <strong>Tên hạng mục:</strong> {eventDetail.itemName}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                {eventDetail.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                      <NotesIcon sx={{ fontSize: 18 }} />
                      Ghi chú
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {eventDetail.notes}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box component="section" aria-label="Thời gian">
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5, fontWeight: 700 }}
                >
                  <AccessTimeIcon sx={{ fontSize: 18 }} />
                  Thời gian
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Bắt đầu</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(eventDetail.startDate, eventDetail.isAllDay)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Kết thúc</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(eventDetail.endDate, eventDetail.isAllDay)}
                    </Typography>
                  </Box>
                </Box>
                {eventDetail.isAllDay && <Chip label="Cả ngày" size="small" variant="outlined" sx={{ mt: 1.5 }} />}
                {eventDetail.isRecurring && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      <strong>Lặp lại:</strong> {getRecurrenceLabel(eventDetail.recurrencePattern)}
                    </Typography>
                    {eventDetail.recurrenceEndDate && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Đến:</strong> {formatDateTime(eventDetail.recurrenceEndDate, true)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box component="section" aria-label="Thông tin tạo">
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5, fontWeight: 700 }}
                >
                  <PersonIcon sx={{ fontSize: 18 }} />
                  Thông tin tạo
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Người tạo</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                      {eventDetail.createdByName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Ngày tạo</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(eventDetail.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                {eventDetail.sharedUsers?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Chia sẻ với ({eventDetail.sharedUsers.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {eventDetail.sharedUsers.map((user) => (
                        <Chip key={user.userId} label={user.fullName} size="small" variant="outlined" title={user.email} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Stack>
          ) : null}
      </AppDialog>

      {/* Delete Confirmation Dialog */}
      <AppConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        title="Xác nhận xóa sự kiện"
        loading={deleting}
        type="delete"
        onConfirm={handleConfirmDelete}
      >
          <Typography>
            Bạn có chắc chắn muốn xóa sự kiện "{eventDetail?.title}" không?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Hành động này không thể hoàn tác.
          </Typography>
      </AppConfirmDialog>
    </>
  );
};

export default EventDetailPopover;
